import {useCallback, useEffect, useRef, DependencyList} from "react";

/**
 * Manages sticky filter-bar height measurement via a CSS custom property.
 * Attaches a ResizeObserver to stickyControlsRef and writes
 * `--sticky-controls-h` to the container element so `thead th` can
 * offset itself below the sticky controls.
 *
 * @param remeasureDeps - values that, when changed, may affect the
 *        sticky controls height (e.g. active filters, search text).
 */
export function useStickyControls(remeasureDeps: DependencyList = []) {
    const containerRef = useRef<HTMLDivElement>(null);
    const stickyControlsRef = useRef<HTMLDivElement>(null);

    const measureStickyHeight = useCallback(() => {
        if (!stickyControlsRef.current || !containerRef.current) return;
        const height = Math.ceil(
            stickyControlsRef.current.getBoundingClientRect().height,
        );
        if (height > 0) {
            containerRef.current.style.setProperty(
                "--sticky-controls-h", `${height}px`,
            );
        }
    }, []);

    useEffect(() => {
        const observer = new ResizeObserver(measureStickyHeight);
        if (stickyControlsRef.current) {
            observer.observe(stickyControlsRef.current);
        }
        return () => observer.disconnect();
    }, [measureStickyHeight]);

    useEffect(() => {
        const timer = setTimeout(measureStickyHeight, 50);
        return () => clearTimeout(timer);
    }, [...remeasureDeps, measureStickyHeight]);

    return {containerRef, stickyControlsRef};
}
